import { useMemo, useState, useCallback } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Button,
  Skeleton,
  SimpleGrid,
  Drawer,
  Divider,
  ActionIcon,
  Tooltip,
  Pagination,
  Modal,
  TextInput,
  NumberInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconBuildingWarehouse,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconEye,
  IconPlus,
  IconArrowDown,
  IconTrash,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import {
  useFixedAssets,
  useCreateAsset,
  useDepreciateAsset,
  useDisposeAsset,
} from '@/hooks/usePortalApi';
import type { FixedAsset } from '@/types';

type SortField = 'name' | 'purchase_cost' | 'book_value' | 'status';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export default function FixedAssets() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [drawerAsset, setDrawerAsset] = useState<FixedAsset | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [disposeOpened, { open: openDispose, close: closeDispose }] = useDisclosure(false);
  const [disposeAssetId, setDisposeAssetId] = useState<number>(0);
  const [disposeProceeds, setDisposeProceeds] = useState(0);
  const [disposeDate, setDisposeDate] = useState<Date | null>(null);

  const [form, setForm] = useState({
    asset_number: '',
    name: '',
    category: 'equipment',
    acquisition_date: null as Date | null,
    acquisition_cost: 0,
    useful_life_months: 60,
    salvage_value: 0,
    depreciation_method: 'straight_line',
    location: '',
  });

  const { data, isLoading } = useFixedAssets();
  const createMutation = useCreateAsset();
  const depreciateMutation = useDepreciateAsset();
  const disposeMutation = useDisposeAsset();

  const allAssets = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allAssets;
    return [...allAssets].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'purchase_cost') cmp = a.purchase_cost - b.purchase_cost;
      else if (sortField === 'book_value') cmp = a.book_value - b.book_value;
      else cmp = a.status.localeCompare(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allAssets, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const assets = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalBookValue = allAssets.reduce((s, a) => s + a.book_value, 0);
  const activeCount = allAssets.filter((a) => a.status === 'active').length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleViewDrawer = useCallback(
    (asset: FixedAsset) => { setDrawerAsset(asset); openDrawer(); },
    [openDrawer],
  );

  const handleDepreciate = useCallback(
    (id: number) => {
      depreciateMutation.mutate(id, {
        onSuccess: () => notifications.show({ title: 'Depreciated', message: 'Depreciation recorded', color: 'green' }),
        onError: () => notifications.show({ title: 'Error', message: 'Depreciation failed', color: 'red' }),
      });
    },
    [depreciateMutation],
  );

  const handleOpenDispose = (id: number) => {
    setDisposeAssetId(id);
    setDisposeProceeds(0);
    setDisposeDate(null);
    openDispose();
  };

  const handleConfirmDispose = () => {
    disposeMutation.mutate(
      { id: disposeAssetId, proceeds: disposeProceeds, disposal_date: disposeDate?.toISOString().split('T')[0] },
      {
        onSuccess: () => { notifications.show({ title: 'Disposed', message: 'Asset disposed', color: 'green' }); closeDispose(); closeDrawer(); },
        onError: () => notifications.show({ title: 'Error', message: 'Disposal failed', color: 'red' }),
      },
    );
  };

  const handleCreate = () => {
    createMutation.mutate(
      {
        asset_number: form.asset_number,
        name: form.name,
        category: form.category,
        acquisition_date: form.acquisition_date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        acquisition_cost: form.acquisition_cost,
        useful_life_months: form.useful_life_months,
        salvage_value: form.salvage_value,
        depreciation_method: form.depreciation_method,
        location: form.location || undefined,
      },
      {
        onSuccess: () => { notifications.show({ title: 'Created', message: 'Asset registered', color: 'green' }); closeCreate(); },
        onError: () => notifications.show({ title: 'Error', message: 'Failed to create asset', color: 'red' }),
      },
    );
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Fixed Assets"
        description="Register, depreciate, and dispose of fixed assets"
        actions={<Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Register Asset</Button>}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard title="Total Book Value" value={formatCurrency(totalBookValue)} icon={<IconBuildingWarehouse size={24} />} />
        <StatCard title="Active Assets" value={String(activeCount)} icon={<IconBuildingWarehouse size={24} />} />
        <StatCard title="Total Assets" value={String(data?.total ?? 0)} icon={<IconBuildingWarehouse size={24} />} />
      </SimpleGrid>

      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : assets.length === 0 ? (
        <EmptyState icon={<IconBuildingWarehouse size={30} />} title="No fixed assets" description="No fixed assets have been registered yet" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="name">Asset Name</ThSort>
                  <Table.Th>Category</Table.Th>
                  <ThSort field="status">Status</ThSort>
                  <ThSort field="purchase_cost"><Text ta="right" inherit>Cost</Text></ThSort>
                  <ThSort field="book_value"><Text ta="right" inherit>Book Value</Text></ThSort>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {assets.map((asset) => (
                  <Table.Tr key={asset.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{asset.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{asset.name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{(asset.category || '').replace(/_/g, ' ')}</Text></Table.Td>
                    <Table.Td><StatusBadge status={asset.status} /></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{formatCurrency(asset.purchase_cost)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={500}>{formatCurrency(asset.book_value)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="View details"><ActionIcon variant="subtle" size="sm" onClick={() => handleViewDrawer(asset)}><IconEye size={16} /></ActionIcon></Tooltip>
                        {asset.status === 'active' && (
                          <>
                            <Tooltip label="Depreciate"><ActionIcon variant="subtle" size="sm" color="blue" onClick={() => handleDepreciate(asset.id)} loading={depreciateMutation.isPending}><IconArrowDown size={16} /></ActionIcon></Tooltip>
                            <Tooltip label="Dispose"><ActionIcon variant="subtle" size="sm" color="red" onClick={() => handleOpenDispose(asset.id)}><IconTrash size={16} /></ActionIcon></Tooltip>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}

      {/* Detail Drawer */}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">{drawerAsset?.name ?? 'Asset Details'}</Text>}>
        {drawerAsset ? (
          <Stack gap="md">
            <StatusBadge status={drawerAsset.status} />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Category</Text><Text size="sm" fw={500}>{(drawerAsset.category || '').replace(/_/g, ' ')}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Purchase Cost</Text><Text size="sm" fw={600}>{formatCurrency(drawerAsset.purchase_cost)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Book Value</Text><Text size="sm" fw={600}>{formatCurrency(drawerAsset.book_value)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Accum. Depreciation</Text><Text size="sm" fw={500}>{formatCurrency(drawerAsset.accumulated_depreciation)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Salvage Value</Text><Text size="sm" fw={500}>{formatCurrency(drawerAsset.salvage_value)}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Useful Life</Text><Text size="sm" fw={500}>{drawerAsset.useful_life_months} months</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Method</Text><Text size="sm" fw={500}>{drawerAsset.depreciation_method.replace(/_/g, ' ')}</Text></div>
              {drawerAsset.location && <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Location</Text><Text size="sm" fw={500}>{drawerAsset.location}</Text></div>}
            </SimpleGrid>
            {drawerAsset.status === 'active' && (
              <>
                <Divider />
                <Group>
                  <Button variant="light" leftSection={<IconArrowDown size={16} />} onClick={() => handleDepreciate(drawerAsset.id)} loading={depreciateMutation.isPending}>Depreciate</Button>
                  <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => handleOpenDispose(drawerAsset.id)}>Dispose</Button>
                </Group>
              </>
            )}
          </Stack>
        ) : (
          <Stack gap="xs">{[...Array(4)].map((_, i) => <Skeleton key={i} height={30} radius="md" />)}</Stack>
        )}
      </Drawer>

      {/* Create Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title={<Text fw={600}>Register New Asset</Text>} size="lg">
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <TextInput label="Asset Number" required value={form.asset_number} onChange={(e) => setForm({ ...form, asset_number: e.currentTarget.value })} />
            <TextInput label="Asset Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Select label="Category" data={['equipment', 'vehicle', 'furniture', 'building', 'land', 'software', 'leasehold_improvement']} value={form.category} onChange={(v) => setForm({ ...form, category: v || 'equipment' })} />
            <Select label="Depreciation Method" data={[{ value: 'straight_line', label: 'Straight Line' }, { value: 'declining_balance', label: 'Declining Balance' }]} value={form.depreciation_method} onChange={(v) => setForm({ ...form, depreciation_method: v || 'straight_line' })} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <DateInput label="Acquisition Date" value={form.acquisition_date} onChange={(v) => setForm({ ...form, acquisition_date: v })} />
            <NumberInput label="Acquisition Cost" prefix="$" value={form.acquisition_cost} onChange={(v) => setForm({ ...form, acquisition_cost: Number(v) || 0 })} min={0} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <NumberInput label="Useful Life (months)" value={form.useful_life_months} onChange={(v) => setForm({ ...form, useful_life_months: Number(v) || 60 })} min={1} />
            <NumberInput label="Salvage Value" prefix="$" value={form.salvage_value} onChange={(v) => setForm({ ...form, salvage_value: Number(v) || 0 })} min={0} />
          </SimpleGrid>
          <TextInput label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.currentTarget.value })} />
          <Button onClick={handleCreate} loading={createMutation.isPending} fullWidth>Register Asset</Button>
        </Stack>
      </Modal>

      {/* Dispose Modal */}
      <Modal opened={disposeOpened} onClose={closeDispose} title={<Text fw={600}>Dispose Asset</Text>}>
        <Stack gap="md">
          <DateInput label="Disposal Date" value={disposeDate} onChange={setDisposeDate} />
          <NumberInput label="Sale Proceeds" prefix="$" value={disposeProceeds} onChange={(v) => setDisposeProceeds(Number(v) || 0)} min={0} />
          <Divider />
          <Button color="red" onClick={handleConfirmDispose} loading={disposeMutation.isPending} fullWidth>Confirm Disposal</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
